import type {
  PaymentMethod,
  RecurringPayment,
  RecurringPaymentDetails,
  RecurringPaymentListItem,
} from '../types.js';

export function filterNonCancelledRecurring<T extends { status: string }>(payments: T[]): T[] {
  return payments.filter((p) => String(p.status).toLowerCase() !== 'cancelled');
}

export function pickPrimaryRecurring(
  list: RecurringPaymentListItem[]
): RecurringPaymentListItem | null {
  const filtered = filterNonCancelledRecurring(list);
  return filtered[0] ?? null;
}

export function getHasSchedule(recurring: RecurringPayment | null | undefined): boolean {
  if (!recurring?.tenancy) return false;
  return recurring.tenancy.has_schedule === true;
}

/** Prefer details tenancy when list item omits `has_schedule`. */
export function getHasScheduleForPrimary(
  primary: RecurringPaymentListItem | null,
  details: RecurringPaymentDetails | null | undefined
): boolean {
  if (getHasSchedule(details ?? undefined)) return true;
  if (primary && getHasSchedule(primary)) return true;
  return false;
}

/** Active recurring UI: not pending activation, not cancelled. */
export function isActivatedRecurring(recurring: RecurringPayment | null | undefined): boolean {
  if (!recurring) return false;
  const st = String(recurring.status).toLowerCase();
  if (st === 'cancelled') return false;
  if (st === 'pending_activation') return false;
  return true;
}

export function isRecurringDetails(r: RecurringPayment): r is RecurringPaymentDetails {
  return 'installments' in r && Array.isArray(r.installments);
}

export function formatRecurringCardLine(pm: PaymentMethod | null | undefined): string {
  if (!pm) return 'Card';
  const scheme = (pm.schema ?? '').toLowerCase();
  let name = 'Card';
  if (scheme.includes('visa')) name = 'Visa';
  else if (scheme.includes('master')) name = 'Mastercard';
  else if (scheme.includes('amex')) name = 'AMEX';
  const last = pm.card_number?.slice(-4) ?? '****';
  return `${name} •••• ${last}`;
}

export function formatNextPaymentDateLabel(iso: string, isPaused: boolean): string {
  if (isPaused) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}
